import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Row, Col } from 'react-grid-system';
import {
  Button,
  OptionButtonGroup,
  LoadingIndicator,
  InputDate,
  Input,
  PageHeader,
  MenuOptionButton,
  PageDetailHeader,
  Dropdown,
  Autocomplete,
} from 'listertech-commons-web';
import SideNavPage from 'components/SideNavPage';

import { callGetVisit, callEditVisit, callAddVisit } from 'api/visits';
import { callGetAgencies, callGetContractorsNonPaged } from 'api/agencies';

const typeDisciplineOptions: string[] = ['PT', 'OT', 'PTA', 'COTA'];

const disciplineTypes = {
  TypesList_PT: [
    'PT OASIS SOC',
    'PT Eval',
    'PT Visit',
    'PT Re-eval',
    'PT OASIS RC',
    'PT Discharge',
    'PT OASIS ROC',
  ],
  TypesList_OT: [
    'OT OASIS SOC',
    'OT Eval',
    'OT Visit',
    'OT Re-eval',
    'OT OASIS RC',
    'OT Discharge',
    'OT OASIS ROC',
  ],
  TypesList_PTA: ['PTA Visit'],
  TypesList_COTA: ['COTA Visit'],
};

const VisitEditPage = () => {
  const { visitId } = useParams();
  const history = useHistory();
  const location = useLocation(); 

  const isDuplicateMode = location.pathname.endsWith('/duplicate');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [errors, setErrors] = useState({});
  const [contractorsData, setContractorsData] = useState([]);
  const [originalVisit, setOriginalVisit] = useState(null);

  const [form, setForm] = useState({
    _id: '',
    patient_name: '',
    patient_address: '',
    patient_city: '',
    patient_state: '',
    patient_zip: '',
    patient_phone: '',
    contractor_name: '',
    apartment: '',
    diagnosis: '',
    agency_id: '',
    day: null,
    typesList: [],
    typeValue: '',
    contractorName: '',
    selectedAgencyName: '',
    contractorsDropdownList: [''],
  });

  const handleChange = (field, value) => {
    if (field === 'typeDiscipline') {
      const typesList = disciplineTypes['TypesList_' + value];
      setForm(prev => ({ ...prev, typesList, typeValue: typesList[0] }));
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const combineDateAndTime = (date, time) => {
    if (!date) return null;
    const [h = '00', m = '00'] = (time || '').split(':');
    const d = new Date(date);
    d.setHours(parseInt(h, 10) || 0, parseInt(m, 10) || 0, 0, 0);
    return d.toISOString();
  };

  const loadData = async () => {
    try {
      const visit = await callGetVisit(visitId);
      setOriginalVisit(visit);
      const agenciesRes = await callGetAgencies();
      setAgencies(agenciesRes.data || []);

      const typeDiscipline = visit.type.split(' ')[0];
      let listName = 'TypesList_';
      const typesList = disciplineTypes[listName + typeDiscipline];

      const contractors = await callGetContractorsNonPaged();
      const contractorsDropdownList = contractors.map(c => c.name);
      setContractorsData(contractors);

      setForm({
        _id: isDuplicateMode ? '' : visit._id, 
        typeDiscipline: visit.type.split(' ')[0],
        patient_name: visit.patient.name,
        patient_address: visit.patient.address,
        patient_city: visit.patient.city,
        patient_state: visit.patient.state,
        patient_zip: visit.patient.zip,
        patient_phone: visit.patient.phone,
        apartment: visit.patient.apartment,
        diagnosis: visit.patient.diagnosis,
        agency_id: visit.agency_id,
        day: new Date(visit.date),
        typesList: typesList,
        typeValue: visit.type,
        selectedAgencyName: visit.agencyName,
        contractorsDropdownList,
        contractor_name: visit.contractor?.name || '',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onSubmit = async () => {
    setErrors({});
    setSaving(true);

    const selectedContractor = contractorsData.find(
      c => c.name === form.contractor_name
    );

    const payload = {
      ...originalVisit, 
      type: form.typeValue,
      date: combineDateAndTime(form.day, form.startTime),
      patient: {
        ...originalVisit.patient, 
        name: form.patient_name,
        address: form.patient_address,
        city: form.patient_city,
        state: form.patient_state,
        zip: form.patient_zip,
        phone: form.patient_phone,
        diagnosis: form.diagnosis,
        apartment: form.apartment,
      },
      contractor_id: selectedContractor?._id || null, 
    };

    try {
      if (isDuplicateMode) {
        delete payload._id;
        delete payload.id;
        delete payload.createdAt;
        delete payload.updatedAt;
        
        await callAddVisit(payload);
      } else {
        payload._id = form._id;
        await callEditVisit(payload);
      }
      history.push(`/visits`);
    } catch (e) {
      setErrors({ submit: e?.message || 'Submission failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingIndicator />;

  return (
    <SideNavPage>
      <Row justify="between">
        <PageHeader>{isDuplicateMode ? 'Duplicate Visit' : 'Edit Visit'}</PageHeader>
        <OptionButtonGroup>
          <MenuOptionButton onClick={() => history.goBack()}>
            Back
          </MenuOptionButton>
        </OptionButtonGroup>
      </Row>

      <PageDetailHeader>Visit Info</PageDetailHeader>
      <Row>
        <Col sm={12} md={4} lg={3} xl={2}>
          <InputDate
            label="Date"
            value={form.day}
            onDateChange={date => handleChange('day', date)}
            format="MM/dd/yyyy"
          />
        </Col>
        <Col sm={6} md={3} lg={2} xl={2}>
          <Dropdown
            label="Discipline"
            name="typeDiscipline"
            value={form.typeDiscipline}
            options={typeDisciplineOptions}
            onChange={ev => handleChange('typeDiscipline', ev.target.value)}
          />
        </Col>
        <Col sm={6} md={4} lg={3} xl={2}>
          <Dropdown
            label="Type"
            name="typeValue"
            value={form.typeValue}
            options={form.typesList}
            onChange={ev => handleChange('typeValue', ev.target.value)}
          />
        </Col>
      </Row>

      <PageDetailHeader>Patient Info</PageDetailHeader>
      <Row>
        <Col sm={12} lg={6} xl={4}>
          <Input
            label="Patient Name"
            value={form.patient_name}
            onChange={e => handleChange('patient_name', e.target.value)}
          />
        </Col>
        <Col sm={12} lg={6} xl={4}>
          <Input
            label="Details"
            value={form.diagnosis}
            onChange={e => handleChange('diagnosis', e.target.value)}
          />
        </Col>
        </Row>

        <Row>
        <Col sm={12} lg={6} xl={4}>
          <Input
            label="Address"
            value={form.patient_address}
            onChange={e => handleChange('patient_address', e.target.value)}
          />
        </Col>
        <Col sm={4} lg={4} xl={2}>
            <Input
              label="Apt #"
              name="apartment"
              value={form.apartment}
              onChange={e => handleChange('apartment', e.target.value)}
            />
          </Col>
        <Col sm={12} lg={4} xl={3}>
          <Input
            label="City"
            value={form.patient_city}
            onChange={e => handleChange('patient_city', e.target.value)}
          />
        </Col>
        <Col sm={12} lg={4} xl={2}>
          <Input
            label="State"
            value={form.patient_state}
            onChange={e => handleChange('patient_state', e.target.value)}
          />
        </Col>
        <Col sm={12} lg={4} xl={2}>
          <Input
            label="ZIP Code"
            value={form.patient_zip}
            onChange={e => handleChange('patient_zip', e.target.value)}
          />
        </Col>
        <Col sm={12} lg={4} xl={2}>
          <Input
            label="Patient Phone"
            value={form.patient_phone}
            onChange={e => handleChange('patient_phone', e.target.value)}
          />
        </Col>
      </Row>

      <PageDetailHeader>Clinician</PageDetailHeader>
      <Row>
          <Col sm={12} md={6} lg={4} xl={3}>
            <Autocomplete
              label="Name"
              items={form.contractorsDropdownList}
              value={form.contractor_name || ''}
              onChange={val => handleChange('contractor_name', val)}
            />
          </Col>
          {/* {agencies && (
            <Col sm={12} md={6} lg={4} xl={3}>
              <Dropdown
                label="Agency"
                name="selectedAgencyName"
                value={form.selectedAgencyName}
                options={agencies.map(
                  a => `${a.name}-(${a.director_email})`,
                )}
                onChange={ev => {
                  const optionValue = ev.target.value;
                  const selectedAgency = agencies.find(
                    a => `${a.name}-(${a.director_email})` === optionValue,
                  );
                  handleChange('selectedAgencyName', optionValue);
                  handleChange('agency_id', selectedAgency?._id);
                }}
              />
            </Col>
          )} */}
      </Row>

      <Row>
        <Button onClick={onSubmit} loading={saving}>
          {isDuplicateMode ? 'Create Duplicate' : 'Save'}
        </Button>
      </Row>
    </SideNavPage>
  );
};

export default VisitEditPage;